<script setup>
import { ref, computed } from 'vue'

// Only show in development mode
const isDev = import.meta.env.DEV
const isExpanded = ref(false)

// Reactive localStorage values
const leadSubmittedRaw = ref(localStorage.getItem('lead_submitted'))
const leadIdValue = ref(localStorage.getItem('lead_id'))
const leadEnabled = import.meta.env.VITE_APP_LEAD

// VITE_APP_LEAD_REQUIRED override simulation
const leadRequiredOverride = ref(localStorage.getItem('devtools_lead_required_override') || '')
const leadRequiredActual = import.meta.env.VITE_APP_LEAD_REQUIRED || 'both'

// Computed current mode (override takes precedence)
const currentLeadRequired = computed(() => {
  return leadRequiredOverride.value || leadRequiredActual
})

// Parse lead submission data
const leadData = computed(() => {
  try {
    if (!leadSubmittedRaw.value) return null
    return JSON.parse(leadSubmittedRaw.value)
  } catch {
    return null
  }
})

// Check if submitted and not expired
const isLeadValid = computed(() => {
  if (!leadData.value) return false

  const now = Date.now()
  const isExpired = now > leadData.value.expiresAt

  return !isExpired
})

// Time until expiration
const timeUntilExpiration = computed(() => {
  if (!leadData.value || !leadData.value.expiresAt) return null

  const now = Date.now()
  const remaining = leadData.value.expiresAt - now

  if (remaining <= 0) return 'Expired'

  const hours = Math.floor(remaining / (60 * 60 * 1000))
  const minutes = Math.floor((remaining % (60 * 60 * 1000)) / (60 * 1000))

  return `${hours}h ${minutes}m`
})

// Computed display values
const shouldShowForm = computed(() => {
  return leadEnabled === 'true' && !isLeadValid.value
})

// Update reactive values
const refreshValues = () => {
  leadSubmittedRaw.value = localStorage.getItem('lead_submitted')
  leadIdValue.value = localStorage.getItem('lead_id')
}

// Clear lead submission flag
const clearLead = () => {
  localStorage.removeItem('lead_submitted')
  localStorage.removeItem('lead_id')
  refreshValues()
  window.location.reload()
}

// Set lead as submitted (with 24h expiration)
const setLead = () => {
  const now = Date.now()
  const data = {
    submitted: true,
    timestamp: now,
    expiresAt: now + 24 * 60 * 60 * 1000,
  }
  localStorage.setItem('lead_submitted', JSON.stringify(data))
  localStorage.setItem('lead_id', 'TEST-ID-' + Math.random().toString(36).substr(2, 9))
  refreshValues()
  window.location.reload()
}

// Clear all localStorage
const clearAll = () => {
  if (confirm('Clear ALL localStorage? This cannot be undone.')) {
    localStorage.clear()
    refreshValues()
    window.location.reload()
  }
}

// Set VITE_APP_LEAD_REQUIRED override
const setLeadRequiredMode = (mode) => {
  if (mode === '') {
    // Clear override (use env var default)
    localStorage.removeItem('devtools_lead_required_override')
    leadRequiredOverride.value = ''
  } else {
    // Set override
    localStorage.setItem('devtools_lead_required_override', mode)
    leadRequiredOverride.value = mode
  }
  window.location.reload()
}

// Toggle expanded state
const toggle = () => {
  isExpanded.value = !isExpanded.value
}

// Format timestamp
const formatTimestamp = (milliseconds) => {
  if (!milliseconds) return 'N/A'
  try {
    return new Date(milliseconds).toLocaleString()
  } catch {
    return 'Invalid'
  }
}
</script>

<template>
  <div v-if="isDev" class="fixed bottom-4 right-4 z-[100]">
    <!-- Toggle Button -->
    <button
      v-if="!isExpanded"
      @click="toggle"
      class="bg-purple-600 hover:bg-purple-700 text-white rounded-full p-3 shadow-lg hover:shadow-xl transition-all"
      title="Open DevTools"
    >
      <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
      </svg>
    </button>

    <!-- DevTools Panel -->
    <div
      v-else
      class="bg-white dark:bg-gray-800 shadow-2xl rounded-lg border border-gray-200 dark:border-gray-700 w-80"
    >
      <!-- Header -->
      <div class="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700 bg-purple-600 text-white rounded-t-lg">
        <h3 class="font-bold text-sm">🛠️ Lead Gen DevTools</h3>
        <button @click="toggle" class="hover:bg-purple-700 rounded p-1 transition-colors">
          <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      <!-- Content -->
      <div class="p-4 space-y-3">
        <!-- Status Info -->
        <div class="space-y-2 text-xs">
          <div class="flex justify-between">
            <span class="text-gray-600 dark:text-gray-400">Feature Enabled:</span>
            <span :class="leadEnabled === 'true' ? 'text-green-600 font-semibold' : 'text-red-600 font-semibold'">
              {{ leadEnabled === 'true' ? 'YES' : 'NO' }}
            </span>
          </div>
          <div class="flex justify-between">
            <span class="text-gray-600 dark:text-gray-400">Lead Valid:</span>
            <span :class="isLeadValid ? 'text-green-600 font-semibold' : 'text-orange-600 font-semibold'">
              {{ isLeadValid ? 'YES' : 'NO' }}
            </span>
          </div>
          <div class="flex justify-between">
            <span class="text-gray-600 dark:text-gray-400">Show Form:</span>
            <span :class="shouldShowForm ? 'text-orange-600 font-semibold' : 'text-green-600 font-semibold'">
              {{ shouldShowForm ? 'YES' : 'NO' }}
            </span>
          </div>

          <div v-if="leadData" class="pt-2 border-t border-gray-200 dark:border-gray-700 space-y-1">
            <div>
              <span class="text-gray-600 dark:text-gray-400">Submitted At:</span>
              <p class="text-gray-900 dark:text-gray-100 text-xs mt-1 font-mono">
                {{ formatTimestamp(leadData.timestamp) }}
              </p>
            </div>
            <div>
              <span class="text-gray-600 dark:text-gray-400">Expires At:</span>
              <p class="text-gray-900 dark:text-gray-100 text-xs mt-1 font-mono">
                {{ formatTimestamp(leadData.expiresAt) }}
              </p>
            </div>
            <div>
              <span class="text-gray-600 dark:text-gray-400">Time Remaining:</span>
              <p :class="[
                'text-xs mt-1 font-semibold',
                timeUntilExpiration === 'Expired' ? 'text-red-600' : 'text-green-600'
              ]">
                {{ timeUntilExpiration }}
              </p>
            </div>
            <div v-if="leadIdValue">
              <span class="text-gray-600 dark:text-gray-400">Lead ID:</span>
              <p class="text-purple-600 dark:text-purple-400 text-xs mt-1 font-mono break-all">
                {{ leadIdValue }}
              </p>
            </div>
          </div>
        </div>

        <!-- VITE_APP_LEAD_REQUIRED Simulator -->
        <div class="space-y-2 pt-2 border-t border-gray-200 dark:border-gray-700">
          <label class="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-2">
            🔧 VITE_APP_LEAD_REQUIRED Mode
          </label>
          <div class="flex items-center gap-2 text-xs">
            <span class="text-gray-600 dark:text-gray-400">Env Default:</span>
            <span class="font-mono px-2 py-0.5 bg-gray-100 dark:bg-gray-700 rounded">
              {{ leadRequiredActual }}
            </span>
          </div>
          <div class="flex items-center gap-2 text-xs">
            <span class="text-gray-600 dark:text-gray-400">Current Mode:</span>
            <span class="font-mono px-2 py-0.5 bg-purple-100 dark:bg-purple-900 text-purple-700 dark:text-purple-300 rounded font-semibold">
              {{ currentLeadRequired }}
            </span>
            <span v-if="leadRequiredOverride" class="text-orange-600 text-xs">
              (Override Active)
            </span>
          </div>

          <div class="grid grid-cols-2 gap-2 mt-3">
            <button
              @click="setLeadRequiredMode('email')"
              :class="[
                'px-3 py-2 rounded text-xs font-medium transition-all',
                currentLeadRequired === 'email'
                  ? 'bg-purple-600 text-white shadow-md ring-2 ring-purple-400'
                  : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
              ]"
            >
              📧 Email Only
            </button>
            <button
              @click="setLeadRequiredMode('phone')"
              :class="[
                'px-3 py-2 rounded text-xs font-medium transition-all',
                currentLeadRequired === 'phone'
                  ? 'bg-purple-600 text-white shadow-md ring-2 ring-purple-400'
                  : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
              ]"
            >
              📱 Phone Only
            </button>
            <button
              @click="setLeadRequiredMode('both')"
              :class="[
                'px-3 py-2 rounded text-xs font-medium transition-all',
                currentLeadRequired === 'both'
                  ? 'bg-purple-600 text-white shadow-md ring-2 ring-purple-400'
                  : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
              ]"
            >
              🔀 Both (Either)
            </button>
            <button
              @click="setLeadRequiredMode('')"
              :class="[
                'px-3 py-2 rounded text-xs font-medium transition-all',
                !leadRequiredOverride
                  ? 'bg-blue-600 text-white shadow-md ring-2 ring-blue-400'
                  : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
              ]"
            >
              🔄 Reset
            </button>
          </div>
        </div>

        <!-- Actions -->
        <div class="space-y-2 pt-2 border-t border-gray-200 dark:border-gray-700">
          <button
            @click="clearLead"
            class="w-full px-3 py-2 bg-red-500 hover:bg-red-600 text-white rounded text-xs font-medium transition-colors"
          >
            🗑️ Clear Lead Flag (Reset)
          </button>
          <button
            @click="setLead"
            class="w-full px-3 py-2 bg-green-500 hover:bg-green-600 text-white rounded text-xs font-medium transition-colors"
          >
            ✅ Set Lead Submitted
          </button>
          <button
            @click="clearAll"
            class="w-full px-3 py-2 bg-orange-500 hover:bg-orange-600 text-white rounded text-xs font-medium transition-colors"
          >
            ⚠️ Clear ALL Storage
          </button>
          <button
            @click="refreshValues"
            class="w-full px-3 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded text-xs font-medium transition-colors"
          >
            🔄 Refresh Values
          </button>
        </div>

        <!-- Instructions -->
        <div class="pt-2 border-t border-gray-200 dark:border-gray-700">
          <p class="text-xs text-gray-500 dark:text-gray-400 italic">
            This panel only shows in development mode
          </p>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
/* Ensure DevTools is always on top */
.z-\[100\] {
  z-index: 100;
}
</style>
